import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { ChevronDown, ChevronUp } from "lucide-react"

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from "@f2e/ui/components/base"

import {
  FetchStatus,
  transformCreditUsageRecord,
  type CreditUsageRecord,
  useGetOrganizationCreditUsageRecordsApi,
} from "@/hooks/api"
import { useToast } from "@/hooks"

const DEFAULT_PAGE_SIZE = 15
type CreditUsageRecordsSortField = "created_at" | "cost_credits"
type CreditUsageRecordsSortDirection = "asc" | "desc"

export interface CreditUsageRecordsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string
  selectedDate: string | null
  workspaceId: string
}

const formatCredits = (value: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

const formatTokens = (value: number) =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(value)

const formatLongDate = (value: string | null) => {
  if (!value) return "-"
  const parsedDate = new Date(`${value}T00:00:00`)
  if (Number.isNaN(parsedDate.getTime())) return value
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(parsedDate)
}

const formatTime = (value: string) => {
  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) return "-"
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(parsedDate)
}

const getRecordActorLabel = (record: CreditUsageRecord) => {
  if (record.userEmail) return record.userEmail
  if (record.clientUserName) return record.clientUserName
  if (record.externalUserId) return record.externalUserId
  return "-"
}

const getRecordTypeLabel = (
  source: string | null | undefined,
  t: ReturnType<typeof useTranslation>["t"]
) => {
  switch (source) {
    case "chat_turn":
      return t("billing.plan.creditUsageRecords.typeLabels.chatTurn")
    case "chat_turn_hitl":
      return t("billing.plan.creditUsageRecords.typeLabels.chatTurnHitl")
    case "chat_turn_evaluation":
      return t("billing.plan.creditUsageRecords.typeLabels.chatTurnEvaluation")
    case "chat_turn_evaluation_hitl":
      return t(
        "billing.plan.creditUsageRecords.typeLabels.chatTurnEvaluationHitl"
      )
    case "chat_turn_internal_agent_call":
      return t(
        "billing.plan.creditUsageRecords.typeLabels.chatTurnInternalAgentCall"
      )
    case "chat_turn_internal_agent_call_hitl":
      return t(
        "billing.plan.creditUsageRecords.typeLabels.chatTurnInternalAgentCallHitl"
      )
    case "chat_turn_evaluation_judge":
      return t(
        "billing.plan.creditUsageRecords.typeLabels.chatTurnEvaluationJudge"
      )
    default:
      return source || "-"
  }
}

const getDisabledReasonLabel = (
  disabledReason: string | null | undefined,
  t: ReturnType<typeof useTranslation>["t"]
) => {
  switch (disabledReason) {
    case "missing_workspace":
      return t(
        "billing.plan.creditUsageRecords.disabledReasons.missingWorkspace"
      )
    case "evaluation_no_history":
      return t(
        "billing.plan.creditUsageRecords.disabledReasons.evaluationNoHistory"
      )
    case "missing_history":
    default:
      return t("billing.plan.creditUsageRecords.disabledReasons.missingHistory")
  }
}

const buildHistoryDetailUrl = (record: CreditUsageRecord) => {
  if (!record.workspaceId || !record.historyId) return null
  const searchParams = new URLSearchParams()
  if (
    record.assistantConversationPartId &&
    Number.isFinite(record.assistantConversationPartId)
  ) {
    searchParams.set(
      "through_part_id",
      String(Math.trunc(record.assistantConversationPartId))
    )
  }

  const queryString = searchParams.toString()
  return `/workspaces/${record.workspaceId}/histories/${record.historyId}${queryString ? `?${queryString}` : ""}`
}

const getOrderBy = (
  sortField: CreditUsageRecordsSortField,
  sortDirection: CreditUsageRecordsSortDirection
) => `${sortDirection === "desc" ? "-" : ""}${sortField}`

const CreditUsageRecordsModal = ({
  open,
  onOpenChange,
  organizationId,
  selectedDate,
  workspaceId,
}: CreditUsageRecordsModalProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { alertToast } = useToast()
  const [page, setPage] = useState(1)
  const [readyScopeKey, setReadyScopeKey] = useState<string | null>(null)
  const [sortField, setSortField] =
    useState<CreditUsageRecordsSortField>("created_at")
  const [sortDirection, setSortDirection] =
    useState<CreditUsageRecordsSortDirection>("desc")
  const {
    data: recordsResp,
    error: recordsError,
    status: recordsStatus,
    fetchFn: fetchRecords,
  } = useGetOrganizationCreditUsageRecordsApi(organizationId)

  const scopeKey = useMemo(() => {
    if (!open || !organizationId || !selectedDate) return null
    return `${organizationId}:${selectedDate}:${workspaceId}`
  }, [open, organizationId, selectedDate, workspaceId])

  useEffect(() => {
    if (!scopeKey) {
      setReadyScopeKey(null)
      return
    }
    setPage(1)
    setSortField("created_at")
    setSortDirection("desc")
    setReadyScopeKey(scopeKey)
  }, [scopeKey])

  useEffect(() => {
    if (!scopeKey || readyScopeKey !== scopeKey) return
    const currentDate = selectedDate
    if (!currentDate) return
    const abortController = new AbortController()
    const params = new URLSearchParams({
      date: currentDate,
      limit: String(DEFAULT_PAGE_SIZE),
      offset: String((page - 1) * DEFAULT_PAGE_SIZE),
      order_by: getOrderBy(sortField, sortDirection),
    })
    if (workspaceId !== "all") {
      params.set("workspace_id", workspaceId)
    }
    fetchRecords({
      searchParams: params,
      signal: abortController.signal,
      preserveData: page > 1,
    })
    return () => {
      abortController.abort()
    }
  }, [
    scopeKey,
    readyScopeKey,
    selectedDate,
    workspaceId,
    page,
    sortField,
    sortDirection,
    fetchRecords,
  ])

  useEffect(() => {
    if (!recordsError) return
    alertToast({
      title: t("billing.plan.errors.loadFailedTitle"),
      description: t("billing.plan.errors.loadFailedDescription"),
      showSupportAction: true,
    })
  }, [recordsError, alertToast, t])

  const records = useMemo(
    () => (recordsResp?.data ?? []).map(transformCreditUsageRecord),
    [recordsResp?.data]
  )
  const pagination = recordsResp?.pagination
  const totalPages = pagination?.total_pages ?? 0
  const currentPage = pagination?.current_page ?? page
  const totalRecords = pagination?.total_records ?? 0
  const isLoadingInitial =
    recordsStatus === FetchStatus.Loading && !recordsResp?.data

  const handleRecordNavigate = (record: CreditUsageRecord) => {
    const targetUrl = buildHistoryDetailUrl(record)
    if (!targetUrl) return
    onOpenChange(false)
    navigate(targetUrl)
  }

  const handleSort = (field: CreditUsageRecordsSortField) => {
    setPage(1)
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
      return
    }
    setSortField(field)
    setSortDirection("desc")
  }

  const renderSortIcon = (field: CreditUsageRecordsSortField) => {
    if (sortField !== field) {
      return <ChevronDown className="h-4 w-4 opacity-30" aria-hidden="true" />
    }
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4" aria-hidden="true" />
    ) : (
      <ChevronDown className="h-4 w-4" aria-hidden="true" />
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>
            {t("billing.plan.creditUsageRecords.title", {
              date: formatLongDate(selectedDate),
            })}
          </DialogTitle>
          <DialogDescription>
            {t("billing.plan.creditUsageRecords.description", {
              count: totalRecords,
            })}
          </DialogDescription>
        </DialogHeader>

        {isLoadingInitial ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : records.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            {t("billing.plan.creditUsageRecords.empty")}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto rounded-md border">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th
                      className="px-3 py-2 text-left font-medium"
                      aria-sort={
                        sortField === "created_at"
                          ? sortDirection === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                    >
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
                        onClick={() => handleSort("created_at")}
                      >
                        {t("billing.plan.creditUsageRecords.columns.time")}
                        {renderSortIcon("created_at")}
                      </button>
                    </th>
                    <th
                      className="px-3 py-2 text-right font-medium"
                      aria-sort={
                        sortField === "cost_credits"
                          ? sortDirection === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                      }
                    >
                      <button
                        type="button"
                        className="inline-flex items-center justify-end gap-1 transition-colors hover:text-foreground"
                        onClick={() => handleSort("cost_credits")}
                      >
                        {t("billing.plan.creditUsageRecords.columns.credits")}
                        {renderSortIcon("cost_credits")}
                      </button>
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t("billing.plan.creditUsageRecords.columns.tokens")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t("billing.plan.creditUsageRecords.columns.model")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t("billing.plan.creditUsageRecords.columns.type")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t("billing.plan.creditUsageRecords.columns.workspace")}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t("billing.plan.creditUsageRecords.columns.actor")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => {
                    const disabledReasonLabel = getDisabledReasonLabel(
                      record.disabledReason,
                      t
                    )

                    return (
                      <tr
                        key={record.id}
                        data-testid={`credit-usage-record-${record.id}`}
                        onClick={() => {
                          if (record.isNavigable) {
                            handleRecordNavigate(record)
                          }
                        }}
                        className={
                          record.isNavigable
                            ? "cursor-pointer border-t transition-colors hover:bg-muted/30"
                            : "border-t bg-muted/10 text-muted-foreground"
                        }
                        title={
                          record.isNavigable ? undefined : disabledReasonLabel
                        }
                      >
                        <td className="px-3 py-3 align-top">
                          <div>{formatTime(record.createdAt)}</div>
                          {!record.isNavigable && (
                            <div
                              className="mt-1 inline-flex rounded-full bg-muted px-2 py-1 text-xs"
                              title={disabledReasonLabel}
                            >
                              {disabledReasonLabel}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right align-top font-medium">
                          {formatCredits(record.costCredits)}
                        </td>
                        <td className="px-3 py-3 text-right align-top">
                          {formatTokens(record.totalTokens)}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="font-medium">
                            {record.modelDisplayName ||
                              record.modelId ||
                              t("billing.plan.creditUsageRecords.unknownModel")}
                          </div>
                          {(record.modelProvider || record.modelId) && (
                            <div className="text-xs text-muted-foreground">
                              {[record.modelProvider, record.modelId]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top">
                          {getRecordTypeLabel(record.source, t)}
                        </td>
                        <td className="px-3 py-3 align-top">
                          {record.workspaceName || "-"}
                        </td>
                        <td className="px-3 py-3 align-top">
                          {getRecordActorLabel(record)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-muted-foreground">
                  {t("billing.plan.creditUsageRecords.pagination", {
                    page: currentPage,
                    totalPages,
                    count: totalRecords,
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  >
                    {t("billing.plan.creditUsageRecords.previous")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() =>
                      setPage((prev) => Math.min(totalPages, prev + 1))
                    }
                  >
                    {t("billing.plan.creditUsageRecords.next")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CreditUsageRecordsModal
